import { UserConnectionActionType, UserConnectionListKey } from '@alltrails/types/belay';
import {
  ActivityCreatedItemData,
  FeedItemType,
  FeedName,
  ItemData,
  ListAdditionItemData,
  PhotosItemData,
  ReviewItemData,
  TrailCompletedItemData
} from '@alltrails/types/blazes';
import { ReactionKind, ReactionSummary } from '@alltrails/types/reactions';
import type CommunityConnectActionLocation from '../../enums/CommunityConnectActionLocation';
import type CommunityConnectRelationshipAction from '../../enums/CommunityConnectRelationshipAction';
import type CommunityNullStateLocation from '../../enums/CommunityNullStateLocation';
import type CommunityPostType from '../../enums/CommunityPostType';
import type CommunityProfileLocation from '../../enums/CommunityProfileLocation';

export const getPostType = (itemType: FeedItemType): CommunityPostType => {
  switch (itemType) {
    case FeedItemType.ActivityCreated:
      return 'activity';
    case FeedItemType.ReviewsCreated:
      return 'review';
    case FeedItemType.TrailCompleted:
      return 'trail';
    case FeedItemType.ActivityPhotosUploaded:
    case FeedItemType.ActivityPhotosUploadedV1:
    case FeedItemType.TrailPhotosUploaded:
    case FeedItemType.TrailPhotosUploadedV1:
      return 'photo';
    case FeedItemType.ListItemAdded:
    case FeedItemType.ListItemAddedV1:
    case FeedItemType.ListCreated:
      return 'list';
    default:
      return '' as CommunityPostType;
  }
};

export const getPostContentId = (postType: CommunityPostType, itemData: ItemData) => {
  switch (postType) {
    case 'review':
      return (itemData as ReviewItemData).review?.id as number;
    case 'activity':
      return (itemData as ActivityCreatedItemData)?.activity?.id as number;
    case 'list':
      return (itemData as ListAdditionItemData)?.list?.id as number;
    case 'photo':
      return (itemData as PhotosItemData)?.photos?.[0]?.id as number;
    case 'trail':
      return (itemData as TrailCompletedItemData)?.trail?.id as number;
    default:
      return 0 as number;
  }
};

export const getNumLikes = (reactionSummary?: ReactionSummary): number => {
  if (reactionSummary) {
    const likeReaction = reactionSummary.reactions.find(reaction => reaction.kind === ReactionKind.Like);
    return likeReaction?.count || 0;
  }
  return 0;
};

export const getNullStateLocation = (activeFeedKey: FeedName | null): CommunityNullStateLocation => {
  switch (activeFeedKey) {
    case FeedName.PERSONAL:
      return 'my posts';
    case FeedName.LOCAL:
      return 'location feed';
    default:
      return 'feed';
  }
};

export const getProfileLocation = (listKey: UserConnectionListKey, isInModal?: boolean): CommunityProfileLocation => {
  switch (listKey) {
    case UserConnectionListKey.FindFriends:
      if (isInModal) {
        return 'synced contacts view all';
      }
      return 'synced contacts connect list';
    case UserConnectionListKey.FollowerRequests:
      if (isInModal) {
        return 'follow requests view all';
      }
      return 'follow requests connect list';
    case UserConnectionListKey.Suggested:
      if (isInModal) {
        return 'suggested members overflow';
      }
      return 'suggested members connect list';
    case UserConnectionListKey.Reactors:
      return 'reactions';
    case UserConnectionListKey.Blocked:
      return 'manage blocked members';
    case UserConnectionListKey.Notifications:
      return 'connect page';
    default:
      return '' as CommunityProfileLocation;
  }
};

export const getItemLocation = (listKey: UserConnectionListKey, isInModal?: boolean): CommunityConnectActionLocation => {
  switch (listKey) {
    case UserConnectionListKey.FindFriends:
      if (isInModal) {
        return 'synced contacts view all';
      }
      return 'synced contacts connect list';
    case UserConnectionListKey.FollowerRequests:
      if (isInModal) {
        return 'follow requests view all';
      }
      return 'follow requests connect list';
    case UserConnectionListKey.Suggested:
      if (isInModal) {
        return 'suggested members view all';
      }
      return 'suggested members connect list';
    case UserConnectionListKey.Reactors:
      return 'reactions';
    case UserConnectionListKey.Blocked:
      return 'manage blocked members';
    case UserConnectionListKey.Notifications:
      return 'connect page';
    case UserConnectionListKey.Feed:
      return 'location feed';
    default:
      return '' as CommunityConnectActionLocation;
  }
};

export const getRelationshipAction = (actionType: UserConnectionActionType): CommunityConnectRelationshipAction => {
  switch (actionType) {
    case UserConnectionActionType.Accept:
      return 'accept-request';
    case UserConnectionActionType.Block:
      return 'block';
    case UserConnectionActionType.Deny:
      return 'deny-request';
    case UserConnectionActionType.Dismiss:
      return 'dismiss';
    case UserConnectionActionType.Follow:
      return 'request-follow';
    case UserConnectionActionType.FollowBack:
      return 'follow-back';
    case UserConnectionActionType.RetractRequest:
      return 'retract-request';
    case UserConnectionActionType.Unblock:
      return 'unblock';
    case UserConnectionActionType.Unfollow:
      return 'unfollow';
    default:
      return '' as CommunityConnectRelationshipAction;
  }
};
